import { useEffect, useRef } from "react";

/**
 * Widget de reserva de Cal.com embebido (autoagenda del paciente). Usa el snippet oficial "vanilla"
 * de Cal.com en vez del paquete @calcom/embed-react para no tener que agregar una dependencia nueva
 * al proyecto — solo inyecta su script una vez y llama a la API global `window.Cal`.
 *
 * Cuando el paciente reserva un horario acá, Cal.com le avisa al backend por webhook
 * (SolicitudCitaController::calcomWebhook) y queda como una "solicitud" pendiente hasta que
 * recepción la apruebe — este componente no crea la cita directamente, solo muestra el calendario.
 */
function loadCalEmbedScript() {
  if (window.Cal) return;
  (function (C, A, L) {
    let p = function (a, ar) {
      a.q.push(ar);
    };
    let d = C.document;
    C.Cal =
      C.Cal ||
      function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () {
            p(api, arguments);
          };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
  })(window, "https://app.cal.com/embed/embed.js", "init");
}

/**
 * @param {string} [calLink] "usuario/evento" de Cal.com (ej. "hans-ribera-2go2ie/30min"). Si no se
 *   pasa, sale de REACT_APP_CALCOM_LINK (env por doctor — ver .env-cmdrc.json) para no hardcodear
 *   la cuenta de un doctor puntual en el código.
 */
function CalBooking({ calLink, className }) {
  const containerRef = useRef(null);
  const link = calLink || process.env.REACT_APP_CALCOM_LINK;

  useEffect(() => {
    if (!link || !containerRef.current) return;
    loadCalEmbedScript();
    window.Cal("init", { origin: "https://cal.com" });
    window.Cal("inline", {
      elementOrSelector: containerRef.current,
      calLink: link,
      layout: "month_view",
      config: { theme: "light" },
    });
  }, [link]);

  if (!link) {
    return (
      <p className="text-sm text-red-500 text-center py-10">
        Falta configurar REACT_APP_CALCOM_LINK para este doctor.
      </p>
    );
  }

  return <div ref={containerRef} className={className || "w-full min-h-[650px]"} />;
}

export default CalBooking;
